import { useState, useRef, useCallback } from 'react';
import { Upload, X, AlertCircle, CheckCircle } from 'lucide-react';

interface LogoUploadProps {
  currentLogoPath?: string | null;
  onUpload: (file: File) => Promise<{ success: boolean; logo_path?: string; message?: string; error?: string; has_transparency?: boolean }>;
  onDelete?: () => Promise<void>;
  disabled?: boolean;
}

export default function LogoUpload({ currentLogoPath, onUpload, onDelete, disabled = false }: LogoUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get full logo URL
  const logoUrl = currentLogoPath
    ? `${import.meta.env.VITE_API_URL || ''}${currentLogoPath}`
    : null;

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!file.type.startsWith('image/png')) {
      return 'Kun PNG-filer er tillatt. SVG støttes ikke i PDF-generering.';
    }

    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      return 'Filen er for stor. Maksimum 5 MB.';
    }

    return null;
  };

  const handleFile = async (file: File) => {
    setError(null);
    setSuccess(null);

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const result = await onUpload(file);
      if (result.success) {
        setSuccess(result.message || 'Logo lastet opp');
        setPreviewUrl(null); // Clear preview, use actual URL
        if (!result.has_transparency) {
          setError('Merk: Bildet har ikke transparent bakgrunn. Dette kan påvirke visningen i attester.');
        }
      } else {
        setError(result.error || 'Kunne ikke laste opp logo');
        setPreviewUrl(null);
      }
    } catch (err: any) {
      setError(err.message || 'Feil ved opplasting');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || uploading) return;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [disabled, uploading]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !currentLogoPath) return;

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      await onDelete();
      setSuccess('Logo slettet');
    } catch (err: any) {
      setError(err.message || 'Kunne ikke slette logo');
    } finally {
      setUploading(false);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const displayUrl = previewUrl || logoUrl;

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Logo
      </label>

      {/* Current/Preview Logo */}
      {displayUrl && (
        <div className="relative inline-block">
          <img
            src={displayUrl}
            alt="Logo"
            className="h-20 max-w-[200px] object-contain bg-gray-50 border rounded-lg p-2"
          />
          {currentLogoPath && onDelete && !disabled && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={uploading}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 disabled:opacity-50"
              title="Slett logo"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${dragActive ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-gray-400'}
          ${disabled || uploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png"
          onChange={handleChange}
          disabled={disabled || uploading}
          className="hidden"
        />

        <div className="space-y-2">
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto" />
              <p className="text-sm text-gray-600">Laster opp...</p>
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-gray-400 mx-auto" />
              <p className="text-sm text-gray-600">
                <span className="font-medium text-orange-600">Klikk for å velge</span> eller dra og slipp
              </p>
              <p className="text-xs text-gray-500">
                PNG, minimum 400x400px, maks 5 MB
              </p>
              <p className="text-xs text-gray-400">
                Anbefalt: PNG med transparent bakgrunn
              </p>
            </>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-2 rounded">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && !error && (
        <div className="flex items-start gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
          <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}
    </div>
  );
}
